import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Bell, Envelope, ArrowsClockwise, Check } from "@phosphor-icons/react";
import { api } from "../lib/api";

const POLL_MS = 60000;

const KIND_ICON = {
  new_lead: Envelope,
  lead_status: ArrowsClockwise,
};

const formatRelative = (iso) => {
  if (!iso) return "";
  try {
    const then = new Date(iso).getTime();
    const diff = Date.now() - then;
    const mins = Math.floor(diff / 60000);
    if (mins < 1) return "ahora";
    if (mins < 60) return `${mins} min`;
    const hrs = Math.floor(mins / 60);
    if (hrs < 24) return `${hrs} h`;
    const days = Math.floor(hrs / 24);
    if (days < 7) return `${days} d`;
    return new Date(iso).toLocaleDateString("es-MX", { day: "numeric", month: "short" });
  } catch { return ""; }
};

export function NotificationBell() {
  const [data, setData] = useState({ items: [], unread_count: 0 });
  const [open, setOpen] = useState(false);
  const navigate = useNavigate();
  const mounted = useRef(true);

  const load = async () => {
    try {
      const { data } = await api.get("/notifications", { params: { limit: 10 } });
      if (mounted.current) setData(data);
    } catch { /* silent */ }
  };

  useEffect(() => {
    mounted.current = true;
    load();
    const t = setInterval(load, POLL_MS);
    return () => { mounted.current = false; clearInterval(t); };
  }, []);

  // Close on outside click
  useEffect(() => {
    if (!open) return;
    const onClick = (e) => {
      if (!e.target.closest("[data-testid='notification-bell-wrapper']")) setOpen(false);
    };
    window.addEventListener("mousedown", onClick);
    return () => window.removeEventListener("mousedown", onClick);
  }, [open]);

  const openItem = async (item) => {
    setOpen(false);
    if (item.kind === "new_lead" || item.kind === "lead_status") {
      // Mark inquiry as read so this notif disappears from the bell.
      try {
        await api.patch(`/inquiries/${item.inquiry_id}/read`, { read: true });
      } catch { /* ignore */ }
      navigate("/dashboard/leads");
      load();
    }
  };

  const markAllRead = async () => {
    const unreadIds = data.items.filter(i => !i.read && i.inquiry_id).map(i => i.inquiry_id);
    // Dedup inquiry ids
    const uniq = Array.from(new Set(unreadIds));
    await Promise.all(uniq.map(id => api.patch(`/inquiries/${id}/read`, { read: true }).catch(() => {})));
    load();
  };

  return (
    <div className="relative" data-testid="notification-bell-wrapper">
      <button
        type="button"
        onClick={() => setOpen(v => !v)}
        aria-label="Notificaciones"
        data-testid="notification-bell-btn"
        className="relative w-10 h-10 rounded-lg border border-[#C8A24A]/20 bg-[#111111] hover:border-[#C8A24A]/50 flex items-center justify-center text-[#C8A24A] transition-colors"
      >
        <Bell size={18} weight={data.unread_count > 0 ? "fill" : "regular"} />
        {data.unread_count > 0 && (
          <span
            data-testid="notification-badge"
            className="absolute -top-1 -right-1 min-w-[18px] h-[18px] px-1 rounded-full bg-[#C8A24A] text-[#111111] text-xs font-bold flex items-center justify-center border-2 border-black"
          >
            {data.unread_count > 9 ? "9+" : data.unread_count}
          </span>
        )}
      </button>

      {open && (
        <div
          data-testid="notification-dropdown"
          className="fixed sm:absolute inset-x-3 sm:inset-auto sm:right-0 top-[64px] sm:top-auto sm:mt-2 sm:w-[360px] bg-[#111111] border border-[#C8A24A]/30 rounded-2xl shadow-2xl overflow-hidden z-50"
        >
          <div className="flex items-center justify-between px-4 py-3 border-b border-[#C8A24A]/10">
            <div>
              <p className="text-xs uppercase tracking-widest text-[#C8A24A]/60 font-semibold">Notificaciones</p>
              <p className="text-xs text-[#C8A24A]/50">Últimos 30 días</p>
            </div>
            {data.unread_count > 0 && (
              <button
                type="button"
                onClick={markAllRead}
                data-testid="notification-mark-all-btn"
                className="text-xs text-[#C8A24A] hover:underline font-semibold inline-flex items-center gap-1"
              >
                <Check size={12} weight="bold" /> Marcar todo leído
              </button>
            )}
          </div>
          <div className="max-h-[420px] overflow-y-auto">
            {data.items.length === 0 ? (
              <div className="p-8 text-center" data-testid="notification-empty">
                <Bell size={32} className="mx-auto text-[#C8A24A]/30 mb-2" />
                <p className="text-sm text-[#C8A24A]/70">Sin novedades por ahora</p>
                <p className="text-xs text-[#C8A24A]/50 mt-1">Cuando llegue un lead, lo verás aquí.</p>
              </div>
            ) : (
              <ul className="divide-y divide-[#C8A24A]/5">
                {data.items.map(item => {
                  const Icon = KIND_ICON[item.kind] || Bell;
                  return (
                    <li key={item.id}>
                      <button
                        type="button"
                        onClick={() => openItem(item)}
                        data-testid={`notification-item-${item.id}`}
                        className={`w-full text-left px-4 py-3 flex gap-3 transition-colors ${
                          !item.read ? "bg-[#C8A24A]/[0.05] hover:bg-[#C8A24A]/[0.1]" : "hover:bg-[#C8A24A]/[0.04]"
                        }`}
                      >
                        <div className={`w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0 ${
                          !item.read ? "bg-[#C8A24A] text-black" : "bg-[#1A1A1A] text-[#C8A24A]/70 border border-[#C8A24A]/20"
                        }`}>
                          <Icon size={16} weight="duotone" />
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className={`text-sm truncate ${!item.read ? "text-[#C8A24A] font-semibold" : "text-[#C8A24A]/80"}`}>
                            {item.title}
                          </p>
                          <p className="text-xs text-[#C8A24A]/60 truncate">{item.subtitle}</p>
                          <p className="text-xs text-[#C8A24A]/40 mt-0.5">{formatRelative(item.created_at)}</p>
                        </div>
                        {!item.read && <span className="w-2 h-2 rounded-full bg-[#C8A24A] flex-shrink-0 mt-1" />}
                      </button>
                    </li>
                  );
                })}
              </ul>
            )}
          </div>
          <div className="border-t border-[#C8A24A]/10 p-2">
            <button
              type="button"
              onClick={() => { setOpen(false); navigate("/dashboard/leads"); }}
              data-testid="notification-open-leads-btn"
              className="w-full text-center py-2 rounded-lg text-xs font-semibold text-[#C8A24A] hover:bg-[#C8A24A]/10 transition-colors"
            >
              Abrir Lead Center →
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
